/**
 * SidebarExtras — contextual content slot for the global left sidebar.
 *
 * Pattern: named-slot via React portal.
 *
 * AppLayout renders <SidebarExtrasSlot /> once inside the sidebar's
 * scroll region. Any page rendered as AppLayout's child can portal
 * arbitrary JSX into that slot via <SidebarExtrasPortal>.
 *
 * This keeps AppLayout agnostic of page-specific chrome (e.g. the
 * calendar's Designation legend and mini-calendar) while letting the
 * page keep full ownership of its data/closures — no prop drilling
 * and no global state.
 */
import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

interface SidebarExtrasContextValue {
  node: HTMLElement | null;
  setNode: (el: HTMLElement | null) => void;
}

const SidebarExtrasContext = createContext<SidebarExtrasContextValue>({
  node: null,
  setNode: () => {
    /* noop */
  },
});

export function SidebarExtrasProvider({ children }: { children: ReactNode }) {
  const [node, setNode] = useState<HTMLElement | null>(null);
  const stableSetNode = useCallback((el: HTMLElement | null) => {
    setNode(el);
  }, []);
  return (
    <SidebarExtrasContext.Provider value={{ node, setNode: stableSetNode }}>
      {children}
    </SidebarExtrasContext.Provider>
  );
}

/**
 * Drop-in target rendered inside the sidebar. Pages portal their
 * contextual content into this element.
 */
export function SidebarExtrasSlot({ className }: { className?: string }) {
  const { setNode } = useContext(SidebarExtrasContext);
  return <div ref={setNode} className={className} data-sidebar-extras-slot />;
}

/**
 * Portal a React subtree into the nearest SidebarExtrasSlot.
 * Returns null silently when no slot is present (e.g. on mobile,
 * where the layout does not render a sidebar).
 */
export function SidebarExtrasPortal({ children }: { children: ReactNode }) {
  const { node } = useContext(SidebarExtrasContext);
  if (!node) return null;
  return createPortal(children, node);
}
